/**
 * Universal Intake — API contract (docs/data-design/INTAKE-PRD.md §2.8.5 / slice
 * U-A). The zod Operations the intake surfaces call through
 * `apiCall`/`useApi` + `createRoute` (rules 1–2 — no raw fetch / NextResponse).
 *
 * U-A ships the read-only `list` operation (the `/database` IntakeBatchesCard).
 * The run / status / preview / confirm / undo operations are authored by their
 * own slices (4 / U-B / U-C) so this file only carries what has a live route.
 *
 * The enums here are the single source of truth for the `intake_batches` CHECK
 * columns AND for `tokens/status.ts` (the `intake` StatusBadge domain imports
 * `IntakeBatchStatus` from here). Keep them in lockstep with the v120 migration.
 */

import { z } from "zod";
import type { Operation } from "../types";

/** `intake_batches.source` — the ingress mode a batch entered through. */
export const intakeSourceSchema = z.enum(["file", "text", "url", "mcp", "cli", "ui"]);
export type IntakeSource = z.infer<typeof intakeSourceSchema>;

/** `intake_batches.status` — the batch lifecycle (owns the `intake` status domain). */
export const intakeBatchStatusSchema = z.enum([
  "landed",
  "classified",
  "awaiting_confirm",
  "awaiting_answer",
  "materialized",
  "undone",
  "failed",
]);
export type IntakeBatchStatus = z.infer<typeof intakeBatchStatusSchema>;

/** `intake_batches.decision` — how a batch reached materialization. */
export const intakeDecisionSchema = z.enum(["auto", "confirmed", "answered", "rejected"]);
export type IntakeDecision = z.infer<typeof intakeDecisionSchema>;

/** One ledger row (mirrors `IntakeBatchLedgerRow` in core `intake/ledger.ts`). */
export const intakeBatchLedgerRowSchema = z.object({
  uid: z.string(),
  source: intakeSourceSchema,
  label: z.string().nullable(),
  status: intakeBatchStatusSchema,
  kind: z.string().nullable(),
  confidence: z.number().nullable(),
  rationale: z.string().nullable(),
  recordsLanded: z.number().int(),
  recordsMaterialized: z.number().int(),
  decision: intakeDecisionSchema.nullable(),
  decidedBy: z.string().nullable(),
  mapperDefUid: z.string().nullable(),
  contentHash: z.string().nullable(),
  needsAttention: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type IntakeBatchLedgerRow = z.infer<typeof intakeBatchLedgerRowSchema>;

/** Workspace roll-up for the card's stat strip. */
export const intakeLedgerStatsSchema = z.object({
  batches7d: z.number().int(),
  rowsLanded: z.number().int(),
  rowsMaterialized: z.number().int(),
  needsAttention: z.number().int(),
});
export type IntakeLedgerStats = z.infer<typeof intakeLedgerStatsSchema>;

export const intakeLedgerSchema = z.object({
  batches: z.array(intakeBatchLedgerRowSchema),
  stats: intakeLedgerStatsSchema,
});
export type IntakeLedgerDto = z.infer<typeof intakeLedgerSchema>;

/** GET /api/intake/list — the `/database` ledger (recent batches + roll-up). */
export const intakeListOp: Operation<void, void, void, IntakeLedgerDto> = {
  method: "GET",
  path: () => "/api/intake/list",
  response: intakeLedgerSchema,
};

/** POST /api/intake/undo — reverse a materialized batch (U-B drill, §2.8.2):
 *  server-real un-map + tombstone (raw stays). Body names the batch by uid. */
export const intakeUndoBody = z.object({ batchUid: z.string().min(1) });
export type IntakeUndoBody = z.infer<typeof intakeUndoBody>;

export const intakeUndoResultSchema = z.object({
  /** Ledger rows tombstoned (un-mapped). */
  reverted: z.number().int(),
  /** The batch's status after the undo (`undone` on success). */
  status: intakeBatchStatusSchema,
});
export type IntakeUndoResult = z.infer<typeof intakeUndoResultSchema>;

export const intakeUndoOp: Operation<void, void, IntakeUndoBody, IntakeUndoResult> = {
  method: "POST",
  path: () => "/api/intake/undo",
  body: intakeUndoBody,
  response: intakeUndoResultSchema,
};

// ─────────────────────────────────────────────────────────────────────────────
// Wizard run seam (INTAKE-PRD §2.8.3 / §2.8.4 / slice U-D). Appended additively
// — the wizard's Automatic mode fires `run`, then polls `status` until the batch
// reaches a terminal/awaiting state (two-phase, §2.8.4), and resolves a held band
// through `confirm`. Kept at the END of the file so the concurrent preview-op
// extension (a different worktree) merges without collision.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The batch snapshot the run/status/confirm ops return — the wire projection of
 * `IntakeResult` (flow.ts) unified with the polled `intake_batches` row so ONE
 * shape serves both the fired result and the poll. `status` is the superset of
 * both status vocabularies: the `IntakeResult` outcomes (`landed_raw`/`duplicate`)
 * and the raw batch lifecycle (`landed`/`classified`/…/`failed`) a mid-flight poll
 * can observe.
 */
export const intakeSnapshotStatusSchema = z.enum([
  "landed",
  "classified",
  "awaiting_confirm",
  "awaiting_answer",
  "materialized",
  "undone",
  "failed",
  "landed_raw",
  "duplicate",
]);
export type IntakeSnapshotStatus = z.infer<typeof intakeSnapshotStatusSchema>;

/** The acting/proposed candidate the band outcome renders (kind + why). */
export const intakeSnapshotCandidateSchema = z.object({
  kind: z.string(),
  confidence: z.number().nullable(),
  rationale: z.string().nullable(),
});
export type IntakeSnapshotCandidate = z.infer<typeof intakeSnapshotCandidateSchema>;

export const intakeSnapshotSchema = z.object({
  batchUid: z.string(),
  status: intakeSnapshotStatusSchema,
  landed: z.number().int(),
  materialized: z.number().int(),
  candidate: intakeSnapshotCandidateSchema.optional(),
  /** The intake connection the batch's raw rows live under — lets a surface build
   *  the drill href (`…/connections/<id>/records?model=intake-<batchUid>`). */
  connectionId: z.number().int().nullable(),
});
export type IntakeSnapshotDto = z.infer<typeof intakeSnapshotSchema>;

/** POST /api/intake/run — fire one intake (file/text/url) through `runIntake` and
 *  return the resolved batch snapshot. Raw always lands first (I1).
 *
 *  `mode` (slice U-E, §2.8.3, additive — default `file`) selects the ingress the
 *  route maps to an `IntakeInput`: `file`/`text` carry `text` (+ `name` as the
 *  label); `url` carries `url` (the client never fetches it — I6; core URL fetch
 *  is slice 6, so a `url` run lands a raw batch that holds honestly). Older
 *  callers that omit `mode`/`url` keep the file behavior unchanged. */
export const intakeRunBody = z.object({
  name: z.string(),
  text: z.string(),
  kind: z.string().optional(),
  yes: z.boolean().optional(),
  mode: z.enum(["file", "text", "url"]).optional(),
  url: z.string().optional(),
});
export type IntakeRunBody = z.infer<typeof intakeRunBody>;

export const intakeRunOp: Operation<void, void, IntakeRunBody, IntakeSnapshotDto> = {
  method: "POST",
  path: () => "/api/intake/run",
  body: intakeRunBody,
  response: intakeSnapshotSchema,
};

/** GET /api/intake/status?batchUid=… — poll a batch's snapshot while the resolve
 *  phase runs server-side (§2.8.4 two-phase progress). */
export const intakeStatusQuery = z.object({ batchUid: z.string().min(1) });
export type IntakeStatusQuery = z.infer<typeof intakeStatusQuery>;

export const intakeStatusOp: Operation<void, IntakeStatusQuery, void, IntakeSnapshotDto> = {
  method: "GET",
  path: () => "/api/intake/status",
  query: intakeStatusQuery,
  response: intakeSnapshotSchema,
};

/** POST /api/intake/confirm — resolve a held batch (§2.8.3 medium/low bands):
 *  accept the top candidate, answer with a chosen candidate kind, or reject
 *  (keep raw). This route NEVER creates an object type — a `__new__` answer is
 *  refused server-side (I4), so it carries no `newTypeLabel`. The explicit
 *  new-type flow lives on `/api/play/answer` (which encodes `__new__:<label>`
 *  in the answer value), never here. */
export const intakeConfirmBody = z.object({
  batchUid: z.string().min(1),
  decision: z.enum(["accept", "answer", "reject"]),
  kind: z.string().optional(),
});
export type IntakeConfirmBody = z.infer<typeof intakeConfirmBody>;

export const intakeConfirmOp: Operation<void, void, IntakeConfirmBody, IntakeSnapshotDto> = {
  method: "POST",
  path: () => "/api/intake/confirm",
  body: intakeConfirmBody,
  response: intakeSnapshotSchema,
};

/** GET /api/intake/preview?batchUid=… — a ≤5-row sample of a batch's landed raw
 *  rows for the Play IntakePreview block (§2.8.5, slice U-C). Read-only. */
export const intakePreviewQuery = z.object({ batchUid: z.string().min(1) });
export type IntakePreviewQuery = z.infer<typeof intakePreviewQuery>;

export const intakePreviewSchema = z.object({
  columns: z.array(z.string()),
  rows: z.array(z.record(z.string(), z.string())),
});
export type IntakePreviewDto = z.infer<typeof intakePreviewSchema>;

export const intakePreviewOp: Operation<void, IntakePreviewQuery, void, IntakePreviewDto> = {
  method: "GET",
  path: () => "/api/intake/preview",
  query: intakePreviewQuery,
  response: intakePreviewSchema,
};

export const intake = {
  list: intakeListOp,
  undo: intakeUndoOp,
  run: intakeRunOp,
  status: intakeStatusOp,
  confirm: intakeConfirmOp,
  preview: intakePreviewOp,
};
